import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { downloadCandidateCV, downloadCandidateAssessmentPdf } from '../services/job.service';
import { Candidate, JobOffer } from '../models/job';
import { downloadBlob, getFileExtension, handleDownloadError, sanitizeFilename } from '../utils/downloadUtils';

export const useCandidateDownload = (candidate: Candidate, job: JobOffer) => {
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation('candidates');

  const [isDownloadingCV, setIsDownloadingCV] = useState(false);
  const [isDownloadingAssessment, setIsDownloadingAssessment] = useState(false);

  const downloadCV = useCallback(async () => {
    if (isDownloadingCV) return;
    setIsDownloadingCV(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await downloadCandidateCV(accessToken, job.id, candidate.id);

      if (!response.data || response.error) {
        throw new Error(response.error || t('downloads.cv_failed'));
      }

      const blob = response.data;
      const extension = getFileExtension(blob.type);
      const filename = `cv_${sanitizeFilename(candidate.talentName)}${extension}`;

      downloadBlob(blob, filename, t('downloads.cv_success'), t('downloads.cv_failed'));
    } catch (error) {
      handleDownloadError(error, t('downloads.cv_failed'), {
        notFound: t('downloads.not_found'),
        forbidden: t('downloads.forbidden'),
      });
    } finally {
      setIsDownloadingCV(false);
    }
  }, [candidate, job.id, getAccessTokenSilently, isDownloadingCV, t]);

  const downloadAssessment = useCallback(async () => {
    if (isDownloadingAssessment) return;
    setIsDownloadingAssessment(true);

    try {
      const accessToken = await getAccessTokenSilently();

      // Call backend API to generate and download PDF
      const response = await downloadCandidateAssessmentPdf(accessToken, candidate.id);

      if (!response.data || response.error) {
        throw new Error(response.error || t('downloads.assessment_failed'));
      }

      const blob = response.data;
      const filename = `assessment_${sanitizeFilename(candidate.talentName)}_${new Date()
        .toISOString()
        .split('T')[0]}.pdf`;

      downloadBlob(
        blob,
        filename,
        t('downloads.assessment_success'),
        t('downloads.assessment_failed')
      );
    } catch (error) {
      handleDownloadError(error, t('downloads.assessment_failed'), {
        notFound: t('downloads.not_found'),
        forbidden: t('downloads.forbidden'),
      });
    } finally {
      setIsDownloadingAssessment(false);
    }
  }, [candidate, getAccessTokenSilently, isDownloadingAssessment, t]);

  return {
    downloadCV,
    downloadAssessment,
    isDownloadingCV,
    isDownloadingAssessment,
  };
};
